import React, { useRef, useState } from "react";
import uploadImageToServer from "../../utils/uploadImageToServer.jsx"; // Adjust if needed

const MAX_SIZE = 5 * 1024 * 1024;

export default function PhotoEvidenceSection({ evidenceFiles = [], setEvidenceFiles }) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleChange = async (e) => {
    setError("");
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length === 0) return;

    setUploading(true);
    const newEvidence = [];

    for (const file of newFiles) {
      if (!/^image\//.test(file.type)) {
        setError("Only image files are allowed.");
        continue;
      }
      if (file.size > MAX_SIZE) {
        setError("Images must be under 5MB.");
        continue;
      }

      try {
        const { url } = await uploadImageToServer(file, "evidence");
        newEvidence.push({
          url,
          name: file.name,
          file // 🔥 Include the raw File object for PDF generation
        });
      } catch (err) {
        console.error("Upload failed:", err);
        setError("Upload failed: " + err.message);
      }
    }

    if (newEvidence.length > 0) {
      setEvidenceFiles((prev) => [...(Array.isArray(prev) ? prev : []), ...newEvidence]);
    }

    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleRemove = (idx) => {
    setEvidenceFiles((prev) =>
      Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []
    );
  };

  const fullUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http")
      ? url
      : `https://regulatoryreformfiresafety.seitechinternational.com${url}`;
  };

  return (
    <fieldset className="fra-fieldset p-4 border rounded-lg mb-6">
      <legend className="font-semibold text-lg mb-2">Photo Evidence / Attachments</legend>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleChange}
        className="mb-2"
        aria-label="Upload photo evidence images"
        disabled={uploading}
      />

      {error && <div className="text-red-600 mb-2 text-sm">{error}</div>}

      <div className="fra-evidence-thumbs flex flex-wrap gap-3">
        {evidenceFiles.length > 0 ? (
          evidenceFiles.map((item, i) => (
            <div
              key={i}
              className="relative w-28 h-28 rounded shadow bg-gray-50 overflow-hidden border"
              tabIndex={0}
              aria-label={`Evidence ${i + 1}`}
            >
              <img
                src={fullUrl(item.url)}
                alt={item.name || `Evidence ${i + 1}`}
                className="object-cover w-full h-full"
                width={112}
                height={112}
                loading="lazy"
              />
              <button
                type="button"
                aria-label="Remove"
                onClick={() => handleRemove(i)}
                className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full px-2 py-0.5 text-xs"
                title="Remove"
              >
                ✕
              </button>
            </div>
          ))
        ) : (
          <div className="text-gray-400 text-sm mt-2">No evidence images yet.</div>
        )}
      </div>

      {uploading && <div className="text-blue-500 mt-2 text-sm">Uploading images...</div>}
    </fieldset>
  );
}
